// A tiny element builder so pages can stay plain TypeScript without a UI framework,
// and patch(), which updates what is on screen in place from a freshly built tree.

type Child = Node | string | number | null | undefined | false;
type Attrs = Record<string, unknown> & { class?: string; style?: string; key?: string | number };

// ------------------------------------------------------------ bookkeeping

/**
 * Nodes h() built in the current task. patch() merges only those into what is
 * on screen; anything older (a search box or a list a page keeps in a field)
 * is placed as it is.
 */
const born = new WeakMap<Node, number>();
let generation = 0;
let bumpQueued = false;
/** `key` of a list item, so patch() follows it when the list is reordered. */
const keys = new WeakMap<Node, string>();
/** Event handlers given to h(), swapped on a patch so they see the new render's data. */
const handlers = new WeakMap<Element, Record<string, EventListener>>();
const listening = new WeakMap<Element, Set<string>>();
/** Properties h() set directly (checked, disabled...), reapplied on a patch. */
const props = new WeakMap<Element, Record<string, unknown>>();
/** Nodes patch() put on screen from a render, which a later render may update in place. */
const owned = new WeakSet<Node>();
/** Containers given to patch(): the page holds them, so they are never merged into something else. */
const pinned = new WeakSet<Node>();
/** Built by an opaque() component: replaced whole instead of merged. */
const opaqueNodes = new WeakSet<Node>();
/** The value (or checked state) the code last rendered into a form control. */
const rendered = new WeakMap<Element, unknown>();
/** Form controls the user typed in or clicked since the code last changed them. */
const edited = new WeakSet<Element>();

interface FocusState {
  el: HTMLElement;
  start: number | null;
  end: number | null;
}
/** Focus held by a node h() moved out of the page while building, put back by the next patch(). */
let movedFocus: FocusState | null = null;

if (typeof document !== "undefined") {
  const mark = (e: Event) => {
    if (e.target instanceof Element && isControl(e.target)) edited.add(e.target);
  };
  document.addEventListener("input", mark, true);
  document.addEventListener("change", mark, true);
}

function markBorn(node: Node): void {
  born.set(node, generation);
  if (bumpQueued) return;
  bumpQueued = true;
  queueMicrotask(() => {
    generation++;
    bumpQueued = false;
  });
}

function isFresh(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE) return !owned.has(node) && !node.isConnected;
  return born.get(node) === generation && !owned.has(node) && !pinned.has(node);
}

function dispatch(this: Element, ev: Event): void {
  handlers.get(this)?.[ev.type]?.call(this, ev);
}

function listen(el: Element, type: string): void {
  let set = listening.get(el);
  if (!set) listening.set(el, (set = new Set()));
  if (set.has(type)) return;
  set.add(type);
  el.addEventListener(type, dispatch);
}

// ------------------------------------------------------------ building

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs | null = null,
  ...children: Array<Child | Child[]>
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  markBorn(el);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value === undefined || value === null || value === false) continue;
      if (key === "key") {
        keys.set(el, String(value));
      } else if (key.startsWith("on") && typeof value === "function") {
        const type = key.slice(2).toLowerCase();
        let map = handlers.get(el);
        if (!map) handlers.set(el, (map = {}));
        map[type] = value as EventListener;
        listen(el, type);
      } else if (key === "class") {
        el.className = String(value);
      } else if (key in el && typeof value !== "string") {
        (el as unknown as Record<string, unknown>)[key] = value;
        let map = props.get(el);
        if (!map) props.set(el, (map = {}));
        map[key] = value;
      } else {
        el.setAttribute(key, value === true ? "" : String(value));
      }
    }
  }
  append(el, children);
  if (isControl(el)) rendered.set(el, controlValue(el));
  else if (el instanceof HTMLDetailsElement) rendered.set(el, el.open);
  return el;
}

export function append(parent: Node, children: Array<Child | Child[]>): void {
  for (const child of children.flat()) {
    if (child === null || child === undefined || child === false) continue;
    if (typeof child === "object") {
      noteFocus(child);
      parent.appendChild(child);
    } else {
      const text = document.createTextNode(String(child));
      parent.appendChild(text);
    }
  }
}

/** Remembers the focus of a node about to leave the page, so patch() can give it back. */
function noteFocus(node: Node): void {
  if (movedFocus || !node.isConnected) return;
  const active = document.activeElement;
  if (active instanceof HTMLElement && active !== document.body && node.contains(active)) movedFocus = focusOf(active);
}

function focusOf(el: HTMLElement): FocusState {
  const field = el as HTMLInputElement;
  let start: number | null = null;
  let end: number | null = null;
  try {
    start = field.selectionStart ?? null;
    end = field.selectionEnd ?? null;
  } catch {
    // Number inputs and the like have no selection.
  }
  return { el, start, end };
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/**
 * Marks a component that must be replaced whole on a patch rather than merged,
 * for one whose own listeners hold on to the elements it built (a chart).
 */
export function opaque<T extends Node>(node: T): T {
  opaqueNodes.add(node);
  return node;
}

// ------------------------------------------------------------ patching

/**
 * Replaces `parent`'s children with `children`, like replaceChildren, but keeps
 * every element that is still there: the ones already on screen are updated in
 * place, so focus, what was typed, ticked or selected, open sections and scroll
 * positions survive a live update.
 *
 * Elements built by h() for this render are matched to the ones on screen by
 * position and tag, or by their `key` attribute when a list can reorder. A form
 * control keeps what the user entered until the code renders a different value
 * for it. Handlers are swapped for the new render's, so a handler must not read
 * another control built in the same render (that one is thrown away): read it
 * from the event, or keep the control in a field.
 */
export function patch(parent: Element, ...children: Array<Child | Child[]>): void {
  pinned.add(parent);
  const active = document.activeElement;
  const focus = movedFocus ?? (active instanceof HTMLElement && active !== document.body && parent.contains(active) ? focusOf(active) : null);
  movedFocus = null;
  const next: Node[] = [];
  for (const child of children.flat()) {
    if (child === null || child === undefined || child === false) continue;
    next.push(typeof child === "object" ? child : document.createTextNode(String(child)));
  }
  morphChildren(parent, next);
  if (focus && focus.el.isConnected && document.activeElement !== focus.el) {
    focus.el.focus({ preventScroll: true });
    if (focus.start !== null && focus.end !== null) {
      try {
        (focus.el as HTMLInputElement).setSelectionRange(focus.start, focus.end);
      } catch {
        // Not a text field.
      }
    }
  }
}

function canTarget(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE) return true;
  return owned.has(node) && !pinned.has(node) && !opaqueNodes.has(node);
}

function sameKind(a: Node, b: Node): boolean {
  if (a.nodeType !== b.nodeType || a.nodeName !== b.nodeName) return false;
  if (a instanceof HTMLInputElement) return a.type === (b as HTMLInputElement).type;
  return true;
}

/** Marks a node put on screen from a render, and everything built with it, as patchable. */
function adopt(node: Node): void {
  if (!isFresh(node)) return;
  owned.add(node);
  for (const child of node.childNodes) adopt(child);
}

function morphChildren(parent: Node, next: Node[]): void {
  const old = [...parent.childNodes];
  const wanted = new Set(next);
  const byKey = new Map<string, Node>();
  for (const o of old) {
    const k = keys.get(o);
    if (k !== undefined && canTarget(o) && !wanted.has(o)) byKey.set(k, o);
  }
  const used = new Set<Node>();
  const targets: Node[] = [];
  let cursor = 0;
  for (const n of next) {
    let target = n;
    if (isFresh(n)) {
      let match: Node | undefined;
      if (!opaqueNodes.has(n)) {
        const k = keys.get(n);
        if (k !== undefined) {
          const o = byKey.get(k);
          if (o && !used.has(o) && sameKind(o, n)) match = o;
        } else {
          for (let i = cursor; i < old.length; i++) {
            const o = old[i];
            if (used.has(o) || keys.has(o) || wanted.has(o) || !canTarget(o) || !sameKind(o, n)) continue;
            match = o;
            cursor = i + 1;
            break;
          }
        }
      }
      if (match) {
        used.add(match);
        morph(match, n);
        target = match;
      } else {
        adopt(n);
      }
    }
    targets.push(target);
  }
  const keep = new Set(targets);
  for (const o of old) if (!keep.has(o)) parent.removeChild(o);
  targets.forEach((t, i) => {
    const current = parent.childNodes[i];
    if (current !== t) parent.insertBefore(t, current ?? null);
  });
}

function morph(o: Node, n: Node): void {
  if (o.nodeType !== Node.ELEMENT_NODE) {
    if (o.nodeValue !== n.nodeValue) o.nodeValue = n.nodeValue;
    return;
  }
  const oe = o as HTMLElement;
  const ne = n as HTMLElement;
  for (const { name } of [...oe.attributes]) {
    if (!ne.hasAttribute(name) && name !== "open") oe.removeAttribute(name);
  }
  for (const { name, value } of [...ne.attributes]) {
    if (name !== "open" && oe.getAttribute(name) !== value) oe.setAttribute(name, value);
  }
  // A <details> the user opened or closed stays that way until the render changes it.
  if (oe instanceof HTMLDetailsElement) {
    const want = (ne as HTMLDetailsElement).open;
    if (rendered.get(oe) !== want) oe.open = want;
    rendered.set(oe, want);
  }

  const before = props.get(oe) ?? {};
  const after = props.get(ne) ?? {};
  for (const [key, value] of Object.entries(after)) {
    if (key === "value" || key === "checked" || key === "selected" || key === "open") continue;
    if ((oe as unknown as Record<string, unknown>)[key] !== value) (oe as unknown as Record<string, unknown>)[key] = value;
  }
  for (const [key, value] of Object.entries(before)) {
    if (!(key in after) && typeof value === "boolean" && key !== "checked" && key !== "selected" && key !== "open") (oe as unknown as Record<string, unknown>)[key] = false;
  }
  props.set(oe, after);

  const hs = handlers.get(ne);
  if (hs) {
    handlers.set(oe, hs);
    for (const type of Object.keys(hs)) listen(oe, type);
  } else handlers.delete(oe);

  const k = keys.get(ne);
  if (k !== undefined) keys.set(oe, k);

  morphChildren(oe, [...ne.childNodes]);
  if (isControl(oe)) syncControl(oe, ne);
}

function isControl(el: Element): el is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
  return (el instanceof HTMLInputElement && el.type !== "file") || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement;
}

function controlValue(el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string | boolean {
  if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) return el.checked;
  return el.value;
}

/**
 * What the user entered wins while the code keeps rendering the same value, and
 * always while the control has focus; a new value from the code replaces it.
 */
function syncControl(o: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, n: Element): void {
  const want = controlValue(n as typeof o);
  const userOwns = o === document.activeElement || (edited.has(o) && rendered.get(o) === want);
  rendered.set(o, want);
  if (userOwns) return;
  edited.delete(o);
  if (controlValue(o) === want) return;
  if (typeof want === "boolean") (o as HTMLInputElement).checked = want;
  else o.value = want;
}

export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): (...args: A) => void {
  let timer: number | undefined;
  return (...args: A) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), ms);
  };
}
